import React from 'react';
import TopBar from './TopBar';
import { Box, CircularProgress, Grid, Paper, Typography } from '@mui/material';
import { ICON_BUTTON_TYPE } from '../../../constants';
import IconButton from '../../../components/IconButton';
import AddAllergiesModal from './AddAllergiesModal';
import { deleteAllergiesApi, getAllergiesApi, getUserAllergiesApi, deleteUserAllergiesApi } from '../../../utils/api';
import { useMutation, useQuery } from 'react-query';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';

const Allergies = ({ user, recipient }) => {
  const { t } = useTranslation();
  const [showAddModal, setShowAddModal] = React.useState(false);
  const [activeEditData, setActiveEditData] = React.useState(null);

  const { data, isLoading, refetch } = useQuery(
    ['getAllergiesApi'],
    () =>
      recipient?._id
        ? getAllergiesApi({ userId: user?._id, recipientId: recipient?._id })
        : getUserAllergiesApi({ userId: user?._id }),
    {
      retry: false,
      cacheTime: 0,
    },
  );

  const deleteAllergiesHandler = useMutation(recipient?._id ? deleteAllergiesApi : deleteUserAllergiesApi, {
    onSuccess: async ({ data }) => {
      refetch();
    },
    onError: (error) => {
      console.log('error =====> ', error);
    },
  });

  const handleDelete = (item) => {
    if (recipient?._id) {
      deleteAllergiesHandler.mutate({ userId: user?._id, recipientId: recipient?._id, allergyId: item?._id });
    } else {
      deleteAllergiesHandler.mutate({ userId: user?._id, allergyId: item?._id });
    }
  };

  const handleShowEdit = (item) => {
    setActiveEditData(item);
    setShowAddModal(true);
  };

  return (
    <Grid>
      <TopBar btnText={t('addAllergy')} onClick={() => setShowAddModal(true)} />

      {isLoading && (
        <Grid marginY={10} container justifyContent={'center'}>
          <CircularProgress size={40} style={{ color: '#26786a' }} />
        </Grid>
      )}

      {!isLoading && isEmpty(data?.data) && (
        <Typography variant="h5" textAlign="center">
          {t('noAllergyFound')}
        </Typography>
      )}

      <Grid container direction={'row'} marginTop="20px" spacing={5}>
        {!isLoading &&
          !isEmpty(data?.data) &&
          data?.data.map((item, idx) => (
            <Grid item xs={12} md={4} lg={4} key={idx}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  height: '100%',
                  p: 3,
                  mt: 2,
                  gap: 2,
                  bgcolor: 'background.paper',
                  borderRadius: 2.5,
                  boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.05)',
                  '@media (max-width: 600px)': {
                    width: '90%',
                    mt: 2,
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                    marginBottom: '10px',
                  }}
                >
                  <Typography variant="h6" component="div">
                    {item?.title}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton type={ICON_BUTTON_TYPE.edit} onClick={() => handleShowEdit(item)} />
                    <IconButton type={ICON_BUTTON_TYPE.delete} onClick={() => handleDelete(item)} />
                  </Box>
                </Box>

                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <Paper
                      sx={{
                        p: 2,
                        bgcolor: '#f5f5f7',
                        borderRadius: 2,
                        boxShadow: 'none',
                        overflow: 'hidden',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {t('reaction')}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {item?.reaction}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper
                      sx={{
                        p: 2,
                        bgcolor: '#f5f5f7',
                        borderRadius: 2,
                        boxShadow: 'none',
                        overflow: 'hidden',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {t('reactionDescription')}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {item?.description}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper
                      sx={{
                        p: 2,
                        bgcolor: '#f5f5f7',
                        borderRadius: 2,
                        boxShadow: 'none',
                        overflow: 'hidden',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {t('antiAllergenic')}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {item?.antiAllergenic}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper
                      sx={{
                        p: 2,
                        bgcolor: '#f5f5f7',
                        borderRadius: 2,
                        boxShadow: 'none',
                        overflow: 'hidden',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {t('dateOfDiagnosis')}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {item?.date}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          ))}
        <AddAllergiesModal
          show={showAddModal}
          onClose={() => {
            setShowAddModal(false);
            setActiveEditData(null);
          }}
          recipient={recipient}
          refetch={refetch}
          activeEditData={activeEditData}
        />
      </Grid>
    </Grid>
  );
};

export default Allergies;
